import {
  Box,
  Text,
  Input,
  Button,
  VStack,
  Checkbox,
  FormLabel,
  FormControl,
  FormErrorMessage,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  InputGroup,
  InputLeftAddon,
  Radio,
  RadioGroup,
  Stack
} from '@chakra-ui/react'
import { FC, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { GoCheck } from 'react-icons/go'
import { GrClearOption } from 'react-icons/gr'

import useFilters from '@hook/shared/use-filters'
import useLayout from '@hook/shared/use-layout'
import useSessions from '@hook/shared/use-sessions'

import Filter from '@component/Shared/Filter'
import SelectAsyncPaginate from '@component/Shared/FilterPagination'
import SelectAsyncPaginateFromApi from '@component/Shared/FilterPaginationApi'
import SelectAsyncPaginateFromApiPost from '@component/Shared/FilterPaginationApiPost'

import { parseCurrency } from '@helper/parseCurrency'

import { ILayoutFiltersProps } from '@base/@types/components/Layout/Filters'

import LayoutFiltersHeader from './Header'

interface IHandleInputValuesProps {
  name: string
  value: string
}

const LayoutFilters: FC<ILayoutFiltersProps> = ({
  filters = [],
  values,
  defaultDate = {
    initialDate: new Date().toISOString(),
    finalDate: new Date().toISOString()
  },
  changedValue,
  changedValueDesiredPeriod,
  loading: submitting,
  canCancel,
  submitLabel = 'Prosseguir',
  onChange,
  onFilter,
  onClearAllFilters,
  heightAll = '100vh'
}) => {
  const { loading, data } = useFilters()
  const { menuToggle } = useLayout()
  const { hasSessions } = useSessions()

  const [counter, setCounter] = useState(0)
  const [change, setChange] = useState(true)
  const [inputValue, setInputValue] = useState('1')

  const [minDate] = useState(() => {
    const today = new Date()
    const min = new Date(today.setDate(today.getDate())) // permitir apenas datas a partir de 5 dias atrás
    return min.toISOString().slice(0, 10) // converter para formato ISO de data e retornar somente a data (YYYY-MM-DD)
  })

  function isDateValid(selectedDate: string | number | Date) {
    const selected = new Date(selectedDate)
    const min = new Date(minDate)
    return selected >= min
  }
  function handleTempChangeevent(event: { target: { value: any } }) {
    const date = event.target.value

    onChange({
      name: 'initialDate',
      value: date
    })
  }

  function handleDateChange(event: { target: { value: any } }) {
    const date = event.target.value

    if (date === '') {
      return
    }
    if (isDateValid(date)) {
      onChange({
        name: 'initialDate',
        value: date
      })
    } else {
      onChange({
        name: 'initialDate',
        value: minDate
      })
    }
  }

  const handleInputValues = ({ name, value }: IHandleInputValuesProps) => {
    const formattedValue = value.replace(/[^0-9]/g, '')

    onChange({
      name,
      value: Number(formattedValue)
    })

    setInputValue(
      parseCurrency({
        value: Number(formattedValue),
        country: 'pt-br',
        minFraction: 0
      })
    )
  }

  const {
    handleSubmit,
    formState: { errors }
  } = useForm()

  useEffect(() => {
    setInputValue(
      parseCurrency({
        value: Number(changedValueDesiredPeriod),
        country: 'pt-br',
        minFraction: 0
      })
    )
  }, [changedValueDesiredPeriod])

  const height = useMemo(
    () =>
      menuToggle
        ? hasSessions
          ? 'calc(100vh - 29px)'
          : '100vh'
        : hasSessions
        ? 'calc(100vh - 170px)'
        : 'calc(100vh - 141px)',
    [menuToggle, hasSessions]
  )

  const removedRadioButton = (id: string, value: any) => {
    setCounter(counter + 1)
    if (counter === 2) {
      onChange({ name: id, value: '' })
      setCounter(0)
      setChange(false)
    } else {
      if (change) {
        onChange({ name: id, value: value })
      } else {
        setChange(true)
      }
    }
  }

  return (
    <form onSubmit={handleSubmit(onFilter)}>
      <Box
        maxH={height}
        h={heightAll}
        overflowY="auto"
        borderRight="solid 1px"
        borderColor="gray.200"
      >
        <LayoutFiltersHeader />

        <VStack
          h="calc(100% - 148px)"
          overflowY="auto"
          p={4}
          spacing={2}
          w="100%"
        >
          <Button
            width="100%"
            paddingY={2}
            background="#9AE6B4"
            transition="all ease .3s"
            _hover={{ filter: 'brightness(0.9)' }}
            onClick={onClearAllFilters}
            leftIcon={<GrClearOption />}
          >
            Limpar Filtros
          </Button>

          {filters.map(filter => {
            if (filter.type === 'checkbox') {
              return (
                <Box key={`filter-${filter.id}`} w="100%" py={1}>
                  <Checkbox
                    isChecked={values[filter.id]}
                    borderColor="blackAlpha.300"
                    isDisabled={filter.isDisabled}
                    icon={<GoCheck />}
                    defaultIsChecked={filter.agroupDefault}
                    fontSize="sm"
                    onChange={e => {
                      onChange({ name: filter.id, value: e.target.checked })
                    }}
                    colorScheme="green"
                    css={{
                      '.css-7sn7zq[data-disabled]': {
                        border: '2px solid #dcdcdc',
                        color: 'lightGray'
                      }
                    }}
                  >
                    <Text fontSize={14}>{filter.label}</Text>
                  </Checkbox>
                </Box>
              )
            }

            if (filter.type === 'radio') {
              return (
                <Box key={`filter-${filter.id}`} w="100%" py={1}>
                  <FormControl
                    id={`radio-${filter.id}`}
                    isRequired={filter.isRequired}
                  >
                    <FormLabel fontSize="sm">{filter.label}</FormLabel>

                    <RadioGroup
                      colorScheme="green"
                      onChange={e => {
                        removedRadioButton(filter.id, e)
                      }}
                      value={values[filter.id]}
                    >
                      <Stack spacing={8} direction="row">
                        {filter.values.map((value: any) => (
                          <Radio
                            colorScheme="green"
                            borderColor="blackAlpha.400"
                            value={value}
                            onClick={() => removedRadioButton(filter.id, value)}
                          >
                            {value}
                          </Radio>
                        ))}
                      </Stack>
                    </RadioGroup>
                  </FormControl>
                </Box>
              )
            }

            if (filter.type === 'input') {
              return (
                <Box key={`filter-${filter.id}`} w="100%" py={1}>
                  <FormControl id="first-name" isRequired={filter.isRequired}>
                    <FormLabel fontSize="sm">{filter.label}</FormLabel>

                    <Input
                      min={filter.min || undefined}
                      isRequired={filter.isRequired}
                      borderColor="blackAlpha.400"
                      max={filter.max || undefined}
                      value={inputValue}
                      defaultValue={
                        filter.defaultValue ? filter.defaultValue : 0
                      }
                      onChange={({ target: { value } }) =>
                        handleInputValues({
                          name: filter.id,
                          value: value
                        })
                      }
                      _hover={{
                        borderColor: 'blackAlpha.500'
                      }}
                    />

                    <FormErrorMessage>
                      {errors.name && errors.name.message}
                    </FormErrorMessage>
                  </FormControl>
                </Box>
              )
            }

            if (filter.type === 'number-input') {
              return (
                <Box key={`filter-${filter.id}`} w="100%" py={1}>
                  <FormControl id="first-name" isRequired={filter.isRequired}>
                    <FormLabel fontSize="sm">{filter.label} </FormLabel>

                    <NumberInput
                      min={filter.min || undefined}
                      isRequired={filter.isRequired}
                      borderColor="blackAlpha.400"
                      max={filter.max || undefined}
                      value={changedValue}
                      defaultValue={
                        filter.defaultValue ? filter.defaultValue : 0
                      }
                      onChange={(_, valueAsNumber) =>
                        onChange({
                          name: filter.id,
                          value: valueAsNumber
                        })
                      }
                      _hover={{
                        borderColor: 'blackAlpha.500'
                      }}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>

                    <FormErrorMessage>
                      {errors.name && errors.name.message}
                    </FormErrorMessage>
                  </FormControl>
                </Box>
              )
            }
            if (filter.type === 'select-pagination') {
              return (
                <FormControl id="select" isRequired={filter.isRequired}>
                  <SelectAsyncPaginate
                    key={`filter-${filter.id + 1}`}
                    label={filter.label}
                    options={filter.options || data[filter.filterId!]}
                    isLoading={loading}
                    isSearchable={filter.pagination}
                    isMulti={filter.isMulti}
                    limit={filter.limit}
                    value={values[filter.id]}
                    isRequired={filter.isRequired}
                    isDisabled={filter.isDisabled || false}
                    onChange={value => {
                      onChange({ name: filter.id, value })
                    }}
                  />
                </FormControl>
              )
            }
            if (filter.type === 'select-api-post') {
              return (
                <FormControl
                  key={`filter-${filter.id}`}
                  id="select"
                  isRequired={filter.isRequired}
                >
                  <SelectAsyncPaginateFromApiPost
                    label={filter.label}
                    apiUrl={filter.apiUrl}
                    idsField={filter.idsField}
                    labelFields={filter.labelFields}
                    isMulti={filter.isMulti}
                    limit={filter.limit}
                    value={values[filter.id]}
                    isRequired={filter.isRequired}
                    isDisabled={filter.isDisabled || false}
                    onChange={value => {
                      onChange({ name: filter.id, value })
                    }}
                  />
                </FormControl>
              )
            }
            if (filter.type === 'select-api') {
              return (
                <FormControl id="select" isRequired={filter.isRequired}>
                  <SelectAsyncPaginateFromApi
                    key={`filter-${filter.id + 1}`}
                    label={filter.label}
                    apiUrl={filter.apiUrl}
                    isLoading={loading}
                    isSearchable={filter.pagination}
                    isMulti={filter.isMulti}
                    limit={filter.limit}
                    value={values[filter.id]}
                    isRequired={filter.isRequired}
                    isDisabled={filter.isDisabled || false}
                    onChange={value => {
                      onChange({ name: filter.id, value })
                    }}
                  />
                </FormControl>
              )
            }
            if (filter.type === 'date-input-one') {
              return (
                <div style={{ marginTop: '4px', width: '100%' }}>
                  <FormLabel fontSize="sm">
                    {filter.label === ''
                      ? 'Data Inicio Projeção'
                      : filter.label}{' '}
                  </FormLabel>

                  <InputGroup size="sm" height={12}>
                    <InputLeftAddon
                      borderRadius={[4]}
                      borderColor="blackAlpha.300"
                      width={10}
                      height={10}
                      bgColor="blackAlpha.200"
                      textColor={'blackAlpha.600'}
                      children="De"
                      justifyContent="center"
                    />
                    <Input
                      type="date"
                      height={10}
                      borderColor="blackAlpha.400"
                      borderRadius={4}
                      textColor={'blackAlpha.600'}
                      fontSize={16}
                      padding={1}
                      paddingLeft={2}
                      paddingRight={2}
                      min={minDate}
                      size="lg"
                      _hover={{
                        borderColor: 'blackAlpha.500'
                      }}
                      value={defaultDate.initialDate}
                      defaultValue={defaultDate.initialDate}
                      onBlur={handleDateChange}
                      onChange={handleTempChangeevent}
                    />
                  </InputGroup>
                </div>
              )
            }

            if (filter.type === 'date-input') {
              return (
                <div style={{ marginTop: '4px', width: '100%' }}>
                  {filter.label !== '' && (
                    <>
                      <FormLabel fontSize="sm">{filter.label} </FormLabel>
                    </>
                  )}

                  <InputGroup size="sm" height={12}>
                    <InputLeftAddon
                      borderRadius={[4]}
                      borderColor="blackAlpha.300"
                      width={10}
                      height={10}
                      bgColor="blackAlpha.200"
                      textColor={'blackAlpha.600'}
                      children="De"
                      justifyContent="center"
                    />
                    <Input
                      type="date"
                      height={10}
                      borderColor="blackAlpha.400"
                      borderRadius={4}
                      textColor={'blackAlpha.600'}
                      fontSize={16}
                      padding={1}
                      paddingLeft={2}
                      paddingRight={2}
                      size="lg"
                      _hover={{
                        borderColor: 'blackAlpha.500'
                      }}
                      value={defaultDate.initialDate}
                      defaultValue={defaultDate.initialDate}
                      onChange={value =>
                        onChange({
                          name: 'initialDate',
                          value: value.target.value
                        })
                      }
                    />
                  </InputGroup>

                  <InputGroup size="sm" height={12} marginY={0}>
                    <InputLeftAddon
                      borderRadius={[4]}
                      borderColor="blackAlpha.300"
                      width={10}
                      height={10}
                      bgColor="blackAlpha.200"
                      textColor={'blackAlpha.600'}
                      children="Até"
                      justifyContent="center"
                    />
                    <Input
                      type="date"
                      height={10}
                      borderColor="blackAlpha.400"
                      borderRadius={4}
                      textColor={'blackAlpha.600'}
                      fontSize={16}
                      padding={1}
                      paddingLeft={2}
                      paddingRight={2}
                      size="lg"
                      _hover={{
                        borderColor: 'blackAlpha.500'
                      }}
                      value={defaultDate.finalDate}
                      defaultValue={defaultDate.finalDate}
                      onChange={value =>
                        onChange({
                          name: 'finalDate',
                          value: value.target.value
                        })
                      }
                    />
                  </InputGroup>
                </div>
              )
            }

            return (
              <>
                <FormControl id="select" isRequired={filter.isRequired}>
                  <Filter
                    key={`filter-${filter.id + 1}`}
                    label={filter.label}
                    options={data[filter.filterId!]}
                    isLoading={loading}
                    isSearchable={filter.pagination}
                    isMulti={filter.isMulti}
                    limit={filter.limit}
                    value={values[filter.id]}
                    isRequired={filter.isRequired}
                    isDisabled={filter.isDisabled || false}
                    onChange={value => {
                      onChange({ name: filter.id, value })
                    }}
                  />
                </FormControl>
              </>
            )
          })}
        </VStack>
        <Box borderTop="solid 1px" borderColor="gray.200" p={4}>
          {canCancel ? (
            <Button
              bg="green.600"
              color="#fff"
              _hover={{
                bg: 'green.500'
              }}
              _active={{
                bg: 'green.500'
              }}
              spinnerPlacement="start"
              loadingText="Aguarde..."
              isLoading={submitting}
              onClick={onFilter}
              isFullWidth
            >
              {submitLabel}
            </Button>
          ) : (
            <Button
              bg="green.600"
              type="submit"
              color="#fff"
              _hover={{
                bg: 'green.500'
              }}
              _active={{
                bg: 'green.500'
              }}
              spinnerPlacement="start"
              loadingText="Aguarde..."
              isLoading={submitting}
              isFullWidth
            >
              {submitLabel}
            </Button>
          )}
        </Box>
      </Box>
    </form>
  )
}

export default LayoutFilters
